import { ReactNode, useCallback, useEffect, useState } from 'react';

import {
  faCircleCheck,
  faCircleNotch,
  faClipboardList,
  faTrash,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import useDeleteThread from '../hooks/useDeleteThread';
import { APIBaseChronos } from '@/api/hosts';
import { Thread } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/Tooltip';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import formatDate from '@/helpers/formatDate';
import { ThreadStatus } from '@/types';

interface ThreadItemProps {
  activeTab: 'previous' | 'published';
  threadText: string;
  threadId: string;
  goToThread: (threadId: string) => void;
  threadStatus: ThreadStatus;
  isActive: boolean;
  createdAt: string;
  createdByEmail?: string;
  createdById: string;
  userId?: string;
}

const ACTIVE_STATUSES: ThreadStatus[] = [ThreadStatus.PlanExecuting, ThreadStatus.PlanLoading];

const Icon = ({ status, icon }: { status: ThreadStatus; icon: ReactNode }) => {
  return ACTIVE_STATUSES.includes(status) ? (
    <FontAwesomeIcon icon={faCircleNotch} className="fa-spin mr-2 h-3 w-3" />
  ) : status === ThreadStatus.PlanLoaded ? (
    <FontAwesomeIcon icon={faClipboardList} className="mr-2 h-3 w-3 text-gray-600" />
  ) : status === ThreadStatus.Failed ? (
    <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2 h-3 w-3 text-red-600" />
  ) : status === ThreadStatus.PlanComplete && icon ? (
    <div className="animate-popInHalf flex items-center">{icon}</div>
  ) : null;
};

const ThreadsBarItem = ({
  activeTab,
  threadText,
  threadId,
  goToThread,
  threadStatus,
  createdAt,
  isActive,
  createdByEmail,
  createdById,
  userId,
}: ThreadItemProps) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ThreadStatus>(threadStatus);
  const [icon, setIcon] = useState<ReactNode>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const caseId = searchParams.get('caseId');
  const { fetchConfigGET } = useGetFetchConfig();
  const navigate = useNavigate();

  const { mutateAsync: deleteThread, isLoading } = useDeleteThread(caseId || '');

  const onDeleteThreadClick = () => setShowDeleteModal(true);
  const handleConfirmDelete = async () => {
    await deleteThread(threadId);
    if (isActive) {
      const params = new URLSearchParams(window.location.search);
      params.delete('threadId');
      navigate({ search: params.toString() });
    }
    setShowDeleteModal(false);
  };

  const fetchThread = useCallback(
    async (threadId: string): Promise<Thread | null> => {
      try {
        const response = await fetch(`${APIBaseChronos}/api/case/${caseId}/thread/${threadId}`, fetchConfigGET);
        if (!response.ok) {
          console.error('Fetching thread failed');
          return null;
        }
        return response.json();
      } catch (error) {
        console.error('Fetching thread failed', error);
        return null;
      }
    },
    [caseId, fetchConfigGET],
  );

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const pollThread = async (threadId: string) => {
      const response = await fetchThread(threadId);
      if (!response) return;
      const currentStatus: ThreadStatus = response.status;

      if (currentStatus === ThreadStatus.PlanLoaded) {
        setIcon(<FontAwesomeIcon icon={faClipboardList} className="mr-2 h-3 w-3 text-gray-600" />);
        setStatus(currentStatus);
      } else if (currentStatus === ThreadStatus.Failed) {
        setIcon(<FontAwesomeIcon icon={faTriangleExclamation} className="mr-2 h-3 w-3 text-red-600" />);
        setStatus(currentStatus);
      } else if (currentStatus === ThreadStatus.PlanComplete) {
        setStatus(currentStatus);
        setIcon(<FontAwesomeIcon icon={faCircleCheck} className="mr-2 h-3 w-3 text-green-600" />);
        setTimeout(() => {
          setIcon(undefined);
        }, 2000);
      }
    };

    if (status === ThreadStatus.PlanExecuting || status === ThreadStatus.PlanLoading) {
      intervalId = setInterval(() => {
        pollThread(threadId);
      }, 4000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, threadId, fetchThread]);

  const canDelete = !ACTIVE_STATUSES.includes(status) && createdById === userId;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={twMerge(
              `group flex cursor-pointer items-center justify-between rounded transition-colors duration-150 hover:bg-gray-100`,
              isActive && 'bg-gray-200',
            )}
          >
            <div
              className={`w-5/6 cursor-pointer overflow-hidden px-2 py-2 text-xs text-ellipsis`}
              onClick={() => threadId && goToThread(threadId)}
            >
              <div className="overflow-hidden text-xs text-ellipsis whitespace-nowrap">{threadText}</div>
            </div>

            <Icon status={status} icon={icon} />
            {canDelete && (
              <FontAwesomeIcon
                icon={isLoading ? faCircleNotch : faTrash}
                onClick={onDeleteThreadClick}
                className={twMerge(
                  'mr-2 h-3 w-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:text-gray-500',
                  isLoading && 'fa-spin opacity-100',
                )}
              />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="w-auto max-w-[600px]">
          <div className="flex flex-col">
            <p className="mb-1 font-semibold">{formatDate(createdAt)}</p>
            {createdByEmail && activeTab === 'published' && <p className="mb-1">Created by {createdByEmail}</p>}
            <p>{threadText}</p>
          </div>
        </TooltipContent>
      </Tooltip>
      <ConfirmationModal
        isOpen={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        title="Delete Thread"
        description={`Are you sure you want to delete this thread?`}
        onConfirm={handleConfirmDelete}
        isLoading={isLoading}
      />
    </>
  );
};

export default ThreadsBarItem;
